<template>
  <div class="container mx-auto px-4 pt-16">
    <h2
      class="text-center mx-auto max-w-6xl text-2xl sm:text-3xl lg:text-4xl mb-10"
      v-html="dataLocale.data.title"
    ></h2>

    <div class="grid grid-cols-12 gap-2 mt-12">
      <div class="col-span-12 lg:col-span-7">
        <div class="mx-auto w-full max-w-lg rounded-2xl bg-white p-2">
          <Disclosure v-slot="{ open }" v-for="question in questions">
            <DisclosureButton
              :class="{ 'mb-6': !open }"
              class="flex w-full justify-between rounded-lg bg-success-soft px-4 py-2 text-left text-lg"
            >
              <span>{{ question.question }}</span>
              <ChevronUpIcon
                :class="open ? 'rotate-180 transform' : ''"
                class="h-5 w-5 text-success"
              />
            </DisclosureButton>
            <DisclosurePanel class="px-4 pt-4 pb-2 text-base">
              {{ question.answer }}
            </DisclosurePanel>
          </Disclosure>
        </div>
      </div>
      <div class="col-span-12 lg:col-span-5 h-full">
        <div class="flex items-center justify-center h-full">
          <nuxt-img
            src="/faq.png"
            height="420px"
            format="webp"
            alt="faq-image-qrdove"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { ChevronUpIcon } from "vue-tabler-icons";
import DataLocale from "~~/config/interface/DataLocale";

interface Questions {
  locale: string;
  data: Array<{
    question: string;
    answer: string;
  }>;
}

const ListQuestion: Array<Questions> = [
  {
    locale: "en",
    data: [
      {
        question: "How long is the free trial?",
        answer:
          "For those who are using a free account, there may be a certain period of use. Currently, we have no plans to limit the trial period",
      },
      {
        question: "Can I try QR Dove for free?",
        answer:
          "Of course! Just create your free account to get started. You will get two free dynamic QR codes and try our online restaurant. If you like, you can continue to use QR Dove Pro by providing us with your payment details.",
      },
      {
        question: "What payment options do you accept?",
        answer:
          "We accept payment via Paypal or payment cards (Visa, Mastercard and more ) through Paypal. Costs will be in USD, and all prices are inclusive of value-added tax.",
      },
      {
        question: "When is my plan ready to use?",
        answer:
          "As soon as you have selected your plan, enter your payment details and place your order! It is easy.",
      },
      {
        question: "How long is the subscription terms?",
        answer: "Monthly or annually",
      },
      {
        question: "How do I cancel my subscription?",
        answer:
          "You may unsubscribe at any time in your account. Or can email contact us.",
      },
      {
        question: "What happens to my QR Code if I cancel?",
        answer:
          "If you decide to cancel your subscription, all your QR Codes will remain active until they expire next month. If you want to access your stats or generate more QR Codes, renew your subscription.",
      },
      {
        question: "Can I switch to a different plan later?",
        answer:
          "Yes, you can upgrade your account at any time. We will then reduce or increase the package price according to your current plan rate.",
      },
      {
        question:
          "Do you have any discounts for nonprofits or educational institutions?",
        answer: "Yes! Please get in touch with us for more information.",
      },
    ],
  },
  {
    locale: "vi",
    data: [
      {
        question: "Chu kỳ dùng thử miễn phí kéo dài bao lâu?",
        answer:
          "Đối với những người dùng tài khoản miễn phí, có thể có một khoảng thời gian sử dụng nhất định. Hiện tại, chúng tôi không có kế hoạch giới hạn thời gian dùng thử.",
      },
      {
        question: "Tôi có thể dùng thử QR Dove miễn phí không?",
        answer:
          "Tất nhiên! Chỉ cần tạo tài khoản miễn phí của bạn để bắt đầu. Bạn sẽ nhận được hai mã QR động miễn phí và thử nghiệm nhà hàng trực tuyến của chúng tôi. Nếu bạn thích, bạn có thể tiếp tục sử dụng QR Dove Pro bằng cách cung cấp thông tin thanh toán của bạn cho chúng tôi.",
      },
      {
        question: "Hình thức thanh toán nào bạn chấp nhận?",
        answer:
          "Chúng tôi chấp nhận thanh toán qua Paypal hoặc thẻ thanh toán (Visa, Mastercard và nhiều hơn nữa) thông qua Paypal. Chi phí sẽ được tính bằng USD, và tất cả các giá cả đã bao gồm thuế giá trị gia tăng.",
      },
      {
        question: "Khi nào gói dịch vụ của tôi sẵn sàng để sử dụng?",
        answer:
          "Ngay khi bạn đã chọn gói dịch vụ, nhập thông tin thanh toán và đặt hàng của bạn! Điều đó rất đơn giản.",
      },
      {
        question: "Thời hạn đăng ký dịch vụ kéo dài bao lâu?",
        answer: "Hàng tháng hoặc hàng  năm.",
      },
      {
        question: "Làm thế nào để hủy đăng ký dịch vụ?",
        answer:
          "Bạn có thể hủy đăng ký bất cứ lúc nào trong tài khoản của mình. Hoặc có thể gửi email cho chúng tôi liên hệ.",
      },
      {
        question:
          "Nếu tôi hủy đăng ký dịch vụ thì mã QR của tôi sẽ xảy ra điều gì?",
        answer:
          "Nếu bạn quyết định hủy đăng ký dịch vụ, tất cả các mã QR của bạn sẽ vẫn hoạt động cho đến khi chúng hết hạn vào tháng tới. Nếu bạn muốn truy cập các thống kê hoặc tạo thêm mã QR, hãy đăng ký lại dịch vụ.",
      },
      {
        question: "Tôi có thể chuyển sang một gói dịch vụ khác sau này không?",
        answer:
          "Có, bạn có thể nâng cấp tài khoản của mình bất cứ lúc nào. Chúng tôi sẽ giảm hoặc tăng giá gói dịch vụ tùy theo tỷ lệ gói dịch vụ hiện tại của bạn.",
      },
      {
        question:
          "Bạn có áp dụng bất kỳ chiết khấu nào cho các tổ chức phi lợi nhuận hoặc các cơ sở giáo dục không?",
        answer: "Có! Vui lòng liên hệ với chúng tôi để biết thêm thông tin.",
      },
    ],
  },
  {
    locale: "es",
    data: [
      {
        question: "¿Cuánto tiempo dura la prueba gratuita?",
        answer:
          "Para aquellos que utilizan una cuenta gratuita, puede haber un cierto período de uso. Actualmente, no tenemos planes para limitar el período de prueba.",
      },
      {
        question: "¿Puedo probar QR Dove gratis?",
        answer:
          "¡Por supuesto! Simplemente cree su cuenta gratuita para comenzar. Obtendrá dos códigos QR dinámicos gratuitos y podrá probar nuestro restaurante en línea. Si le gusta, puede continuar utilizando QR Dove Pro proporcionándonos sus detalles de pago.",
      },
      {
        question: "¿Qué opciones de pago aceptan?",
        answer:
          "Aceptamos pagos a través de PayPal o tarjetas de pago (Visa, Mastercard y más) a través de PayPal. Los costos serán en USD, y todos los precios incluyen impuestos al valor agregado.",
      },
      {
        question: "¿Cuándo estará listo mi plan para usar?",
        answer:
          "¡Tan pronto como haya seleccionado su plan, ingresado sus detalles de pago y realizado su pedido! Es fácil.",
      },
      {
        question: "¿Cuánto tiempo dura el período de suscripción?",
        answer: "Mensual o anualmente",
      },
      {
        question: "¿Cómo cancelo mi suscripción?",
        answer:
          "Puede cancelar su suscripción en cualquier momento en su cuenta. O puede enviarnos un correo electrónico para contactarnos.",
      },
      {
        question: "¿Qué sucede con mi código QR si cancelo?",
        answer:
          "Si decide cancelar su suscripción, todos sus códigos QR permanecerán activos hasta que expiren el próximo mes. Si desea acceder a sus estadísticas o generar más códigos QR, renueve su suscripción.",
      },
      {
        question: "¿Puedo cambiar a otro plan de servicio más adelante?",
        answer:
          "Sí, puede actualizar su cuenta en cualquier momento. Luego, ajustaremos el precio del paquete reduciéndolo o aumentándolo según la tarifa actual de su plan.",
      },
      {
        question:
          "¿Tienen algún descuento para organizaciones sin fines de lucro o instituciones educativas?",
        answer:
          "¡Sí! Por favor, póngase en contacto con nosotros para obtener más información.",
      },
    ],
  },
  {
    locale: "fr",
    data: [
      {
        question: "Combien de temps dure l'essai gratuit ?",
        answer:
          "Pour ceux qui utilisent un compte gratuit, il peut y avoir une certaine période d'utilisation. Actuellement, nous n'avons aucun plan pour limiter la durée d'essai.",
      },
      {
        question: "Puis-je essayer QR Dove gratuitement ?",
        answer:
          "Bien sûr ! Il vous suffit de créer votre compte gratuit pour commencer. Vous obtiendrez deux codes QR dynamiques gratuits et pourrez essayer notre restaurant en ligne. Si vous le souhaitez, vous pouvez continuer à utiliser QR Dove Pro en nous fournissant vos coordonnées de paiement.",
      },
      {
        question: "Quelles options de paiement acceptez-vous ?",
        answer:
          "Nous acceptons les paiements par PayPal ou cartes de paiement (Visa, Mastercard et plus) via PayPal. Les coûts seront en USD, et tous les prix sont inclusifs de la taxe sur la valeur ajoutée.",
      },
      {
        question: "Quand mon plan sera-t-il prêt à être utilisé ?",
        answer:
          "Dès que vous avez sélectionné votre plan, saisi vos informations de paiement et passé votre commande ! C'est facile.",
      },
      {
        question: "Quelle est la durée de l'abonnement ?",
        answer: "Mensuel ou annuellement",
      },
      {
        question: "Comment puis-je annuler mon abonnement ?",
        answer:
          "Vous pouvez vous désabonner à tout moment dans votre compte. Ou vous pouvez nous contacter par e-mail.",
      },
      {
        question:
          "Que se passe-t-il avec mon code QR si j'annule mon abonnement ?",
        answer:
          "Si vous décidez d'annuler votre abonnement, tous vos codes QR resteront actifs jusqu'à leur expiration le mois suivant. Si vous souhaitez accéder à vos statistiques ou générer plus de codes QR, renouvelez votre abonnement.",
      },
      {
        question: "Puis-je passer à un plan différent plus tard ?",
        answer:
          "Oui, vous pouvez mettre à niveau votre compte à tout moment. Nous réduirons ou augmenterons ensuite le prix du forfait en fonction de votre tarif de plan actuel.",
      },
      {
        question:
          "Offrez-vous des remises aux organismes sans but lucratif ou aux établissements d'enseignement ?",
        answer: "Oui ! Veuillez nous contacter pour plus d'informations.",
      },
    ],
  },
  {
    locale: "de",
    data: [
      {
        question: "Wie lange dauert die kostenlose Testphase?",
        answer:
          "Für diejenigen, die ein kostenloses Konto verwenden, kann es eine bestimmte Nutzungszeit geben. Derzeit haben wir keine Pläne, die Testphase zu begrenzen.",
      },
      {
        question: "Kann ich QR Dove kostenlos ausprobieren?",
        answer:
          "Natürlich! Erstellen Sie einfach Ihr kostenloses Konto, um loszulegen. Sie erhalten zwei kostenlose dynamische QR-Codes und können unser Online-Restaurant ausprobieren. Wenn Ihnen das gefällt, können Sie QR Dove Pro weiterhin nutzen, indem Sie uns Ihre Zahlungsdetails zur Verfügung stellen.",
      },
      {
        question: "Welche Zahlungsoptionen akzeptieren Sie?",
        answer:
          "Wir akzeptieren Zahlungen per PayPal oder Zahlungskarten (Visa, Mastercard und mehr) über PayPal. Die Kosten werden in USD berechnet und alle Preise sind inklusive Mehrwertsteuer.",
      },
      {
        question: "Wann ist mein Plan einsatzbereit?",
        answer:
          "Sobald Sie Ihren Plan ausgewählt, Ihre Zahlungsdetails eingegeben und Ihre Bestellung aufgegeben haben! Es ist einfach.",
      },
      {
        question: "Wie lange sind die Abonnementbedingungen?",
        answer: "Monatlich oder jährlich",
      },
      {
        question: "Wie kann ich mein Abonnement kündigen?",
        answer:
          "Sie können Ihr Abonnement jederzeit in Ihrem Konto kündigen. Oder Sie können uns per E-Mail kontaktieren.",
      },
      {
        question: "Was passiert mit meinem QR-Code, wenn ich kündige?",
        answer:
          "Wenn Sie sich entscheiden, Ihr Abonnement zu kündigen, bleiben alle Ihre QR-Codes aktiv, bis sie nächsten Monat ablaufen. Wenn Sie auf Ihre Statistiken zugreifen oder weitere QR-Codes generieren möchten, erneuern Sie Ihr Abonnement.",
      },
      {
        question: "Kann ich später auf einen anderen Serviceplan wechseln?",
        answer:
          "Ja, Sie können Ihr Konto jederzeit aktualisieren. Wir werden dann den Paketpreis entsprechend Ihrer aktuellen Planrate reduzieren oder erhöhen.",
      },
      {
        question:
          "Gibt es Rabatte für gemeinnützige Organisationen oder Bildungseinrichtungen?",
        answer: "Ja! Bitte kontaktieren Sie uns für weitere Informationen.",
      },
    ],
  },
  {
    locale: "it",
    data: [
      {
        question: "Quanto dura la prova gratuita?",
        answer:
          "Per coloro che utilizzano un account gratuito, potrebbe esserci un certo periodo di utilizzo. Attualmente, non abbiamo piani per limitare il periodo di prova.",
      },
      {
        question: "Posso provare QR Dove gratuitamente?",
        answer:
          "Certo! Basta creare un account gratuito per iniziare. Otterrai due codici QR dinamici gratuiti e potrai provare il nostro ristorante online. Se ti piace, puoi continuare a utilizzare QR Dove Pro fornendoci i tuoi dati di pagamento.",
      },
      {
        question: "Quali opzioni di pagamento accettate?",
        answer:
          "Accettiamo pagamenti tramite PayPal o carte di pagamento (Visa, Mastercard e altro) tramite PayPal. I costi saranno in USD, e tutti i prezzi sono inclusivi di IVA.",
      },
      {
        question: "Quando il mio piano sarà pronto per l'uso?",
        answer:
          "Non appena avrai selezionato il tuo piano, inserito i dati di pagamento e effettuato l'ordine! È facile.",
      },
      {
        question: "Qual è la durata dell'abbonamento?",
        answer: "Mensile o annuale",
      },
      {
        question: "Come posso cancellare il mio abbonamento?",
        answer:
          "Puoi disiscriverti in qualsiasi momento nel tuo account. Oppure puoi contattarci via email.",
      },
      {
        question: "Cosa succede al mio codice QR se cancello l'abbonamento?",
        answer:
          "Se decidi di cancellare il tuo abbonamento, tutti i tuoi codici QR rimarranno attivi fino alla loro scadenza il mese successivo. Se vuoi accedere alle tue statistiche o generare altri codici QR, rinnova il tuo abbonamento.",
      },
      {
        question: "Posso passare a un piano diverso in seguito?",
        answer:
          "Sì, puoi aggiornare il tuo account in qualsiasi momento. Ridurremo o aumenteremo quindi il prezzo del pacchetto in base al tuo attuale tasso di piano.",
      },
      {
        question:
          "Offrite sconti per organizzazioni no-profit o istituzioni educative?",
        answer: "Sì! Contattaci per maggiori informazioni.",
      },
    ],
  },
  {
    locale: "nl",
    data: [
      {
        question: "Hoe lang is de gratis proefperiode?",
        answer:
          "Voor degenen die een gratis account gebruiken, kan er een bepaalde periode van gebruik zijn. Op dit moment hebben we geen plannen om de proefperiode te beperken.",
      },
      {
        question: "Kan ik QR Dove gratis uitproberen?",
        answer:
          "Natuurlijk! Maak gewoon uw gratis account aan om aan de slag te gaan. U krijgt twee gratis dynamische QR-codes en kunt onze online restaurant uitproberen. Als u wilt, kunt u doorgaan met het gebruik van QR Dove Pro door ons uw betalingsgegevens te verstrekken.",
      },
      {
        question: "Welke betalingsmogelijkheden accepteert u?",
        answer:
          "We accepteren betaling via Paypal of betaalkaarten (Visa, Mastercard en meer) via Paypal. Kosten zullen in USD zijn, en alle prijzen zijn inclusief toegevoegde waarde belasting.",
      },
      {
        question: "Wanneer is mijn plan klaar voor gebruik?",
        answer:
          "Zodra u uw plan heeft geselecteerd, voert u uw betalingsgegevens in en plaatst u uw bestelling! Het is eenvoudig.",
      },
      {
        question: "Hoe lang zijn de abonnementstermijnen?",
        answer: "Maandelijks of jaarlijks",
      },
      {
        question: "Hoe kan ik mijn abonnement opzeggen?",
        answer:
          "U kunt op elk moment uw abonnement opzeggen in uw account. Of u kunt ons een e-mail sturen om contact op te nemen.",
      },
      {
        question: "Wat gebeurt er met mijn QR-code als ik opzeg?",
        answer:
          "Als u besluit uw abonnement op te zeggen, blijven al uw QR-codes actief totdat ze volgende maand verlopen. Als u uw statistieken wilt raadplegen of meer QR-codes wilt genereren, vernieuwt u uw abonnement.",
      },
      {
        question: "Kan ik later overschakelen naar een ander abonnement?",
        answer:
          "Ja, u kunt op elk moment uw account upgraden. We zullen dan de pakketprijs verminderen of verhogen volgens uw huidige plan tarief.",
      },
      {
        question:
          "Heeft u kortingen voor non-profitorganisaties of onderwijsinstellingen?",
        answer: "Ja! Neem contact met ons op voor meer informatie.",
      },
    ],
  },
  {
    locale: "ja",
    data: [
      {
        question: "無料トライアルはどのくらいですか？",
        answer:
          "無料アカウントを使用している場合、一定の利用期間がある場合があります。現時点では、トライアル期間を制限する予定はありません。",
      },
      {
        question: "QR Doveを無料で試すことはできますか？",
        answer:
          "もちろんです！ 無料アカウントを作成して始めてください。 2つの無料のダイナミックQRコードを取得し、オンラインレストランを試すことができます。 もし気に入った場合は、お支払いの詳細を提供してQR Dove Proを引き続き使用することができます。",
      },
      {
        question: "どのような支払い方法がありますか？",
        answer:
          "PaypalまたはPaypalを介した支払いカード（Visa、Mastercardなど）で支払いを受け付けています。 費用はUSDで、すべての価格に付加価値税が含まれています。",
      },
      {
        question: "私のプランはいつ使用できますか？",
        answer:
          "プランを選択し、お支払いの詳細を入力し、ご注文いただくとすぐにご利用いただけます！ 簡単です。",
      },
      {
        question: "定期購読期間はどのくらいですか？",
        answer: "毎月",
      },
      {
        question: "定期購読をキャンセルするにはどうすればよいですか？",
        answer:
          "いつでもアカウントで定期購読を解約できます。 また、お問い合わせいただくか、メールでご連絡いただけます。",
      },
      {
        question: "キャンセルした場合、私のQRコードはどうなりますか？",
        answer:
          "定期購読をキャンセルすることを決定した場合、すべてのQRコードは来月まで有効なままです。 統計を参照したり、より多くのQRコードを生成したい場合は、定期購読を更新してください。",
      },
      {
        question: "後で別のプランに切り替えることはできますか？",
        answer:
          "はい、いつでもアカウントをアップグレードできます。 その後、現在のプランレートに応じてパッケージ価格を減額または増額します。",
      },
      {
        answer: "「はい！詳細についてはお問い合わせください。」",
        question: "「非営利団体や教育機関向けの割引はありますか？」",
      },
    ],
  },
  {
    locale: "pt",
    data: [
      {
        question: "Qual é o período de teste gratuito?",
        answer:
          "Para aqueles que estão usando uma conta gratuita, pode haver um certo período de uso. Atualmente, não temos planos para limitar o período de teste",
      },
      {
        question: "Posso experimentar o QR Dove gratuitamente?",
        answer:
          "Claro! Basta criar sua conta gratuita para começar. Você receberá dois códigos QR dinâmicos gratuitos e experimentará nosso restaurante online. Se você gostar, pode continuar a usar o QR Dove Pro fornecendo-nos seus detalhes de pagamento.",
      },
      {
        question: "Quais opções de pagamento vocês aceitam?",
        answer:
          "Aceitamos pagamento via Paypal ou cartões de pagamento (Visa, Mastercard e mais) através do Paypal. Os custos serão em USD, e todos os preços são inclusivos de imposto sobre valor agregado.",
      },
      {
        question: "Quando meu plano estará pronto para usar?",
        answer:
          "Assim que você selecionar seu plano, inserir seus detalhes de pagamento e fazer seu pedido! É fácil.",
      },
      {
        question: "Qual é o prazo de assinatura?",
        answer: "Mensal ou anual",
      },
      {
        question: "Como faço para cancelar minha assinatura?",
        answer:
          "Você pode cancelar a qualquer momento em sua conta. Ou pode entrar em contato conosco por e-mail.",
      },
      {
        question: "O que acontece com meu código QR se eu cancelar?",
        answer:
          "Se você decidir cancelar sua assinatura, todos os seus códigos QR permanecerão ativos até expirarem no próximo mês. Se você quiser acessar suas estatísticas ou gerar mais códigos QR, renove sua assinatura.",
      },
      {
        question: "Posso mudar para um plano diferente mais tarde?",
        answer:
          "Sim, você pode atualizar sua conta a qualquer momento. Em seguida, reduziremos ou aumentaremos o preço do pacote de acordo com a taxa de seu plano atual.",
      },
      {
        question:
          "Vocês oferecem descontos para organizações sem fins lucrativos ou instituições educacionais?",
        answer: "Sim! Entre em contato conosco para obter mais informações.",
      },
    ],
  },
  {
    locale: "zh",
    data: [
      {
        question: "免费试用期有多长时间？",
        answer:
          "对于使用免费账户的用户，可能会有一定的使用期限。目前，我们没有限制试用期的计划。",
      },
      {
        question: "我可以免费试用QR Dove吗？",
        answer:
          "当然可以！只需创建您的免费账户即可开始使用。您将获得两个免费的动态QR代码并尝试我们的在线餐厅。如果您喜欢，您可以继续使用QR Dove Pro，提供您的付款详细信息。",
      },
      {
        question: "您接受哪些付款方式？",
        answer:
          "我们通过Paypal或支付卡（Visa，Mastercard等）接受付款。成本将以美元计算，所有价格均包含增值税。",
      },
      {
        question: "我的计划何时可以使用？",
        answer: "只要您选择了计划，输入您的付款详细信息并下订单！很容易。",
      },
      {
        question: "订阅期限有多长时间？",
        answer: "每月或每年",
      },
      {
        question: "如何取消订阅？",
        answer:
          "您可以在您的账户中随时取消订阅。或者可以通过电子邮件与我们联系。",
      },
      {
        question: "如果我取消订阅，我的QR代码会怎样？",
        answer:
          "如果您决定取消订阅，所有您的QR代码将保持活动状态，直到下个月到期。如果您想访问统计数据或生成更多的QR代码，请续订您的订阅。",
      },
      {
        question: "我可以随时更改到不同的计划吗？",
        answer:
          "是的，您可以随时升级您的账户。然后，我们将根据您当前的计划费率减少或增加套餐价格。",
      },
      {
        question: "您有任何非营利组织或教育机构的折扣吗？",
        answer: "是的！请与我们联系以获取更多信息。",
      },
    ],
  },
  {
    locale: "hi",
    data: [
      {
        question: "मुफ्त परीक्षण कितने समय का होता है?",
        answer:
          "जो लोग मुफ्त खाते का उपयोग कर रहे हैं, उनके लिए उपयोग की एक निश्चित अवधि हो सकती है। फिलहाल, हमारी परीक्षण अवधि को सीमित करने की कोई योजना नहीं है।",
      },
      {
        question: "क्या मैं QR Dove को मुफ्त में आज़मा सकता हूँ?",
        answer:
          "बिल्कुल! आरंभ करने के लिए बस अपना मुफ्त खाता बनाएं। आपको दो मुफ्त गतिशील क्यूआर कोड मिलेंगे और हमारे ऑनलाइन रेस्तरां को आज़माएँ। यदि आप चाहें, तो आप हमें अपने भुगतान विवरण प्रदान करके क्यूआर डव प्रो का उपयोग जारी रख सकते हैं।",
      },
      {
        question: "आप कौन से भुगतान विकल्प स्वीकार करते हैं?",
        answer:
          "हम पेपैल के माध्यम से पेपैल या भुगतान कार्ड (वीज़ा, मास्टरकार्ड और अधिक) के माध्यम से भुगतान स्वीकार करते हैं। लागत USD में होगी, और सभी कीमतों में मूल्य वर्धित कर शामिल है।",
      },
      {
        question: "मेरी योजना कब उपयोग के लिए तैयार है?",
        answer:
          "जैसे ही आपने अपनी योजना का चयन कर लिया है, अपना भुगतान विवरण दर्ज करें और अपना ऑर्डर दें! यह आसान है।",
      },
      {
        question: "सदस्यता की शर्तें कितनी लंबी हैं?",
        answer: "मासिक या वार्षिक",
      },
      {
        question: "मैं अपनी सदस्यता कैसे रद्द करूँ?",
        answer:
          "आप अपने खाते में किसी भी समय सदस्यता समाप्त कर सकते हैं। या हमें ईमेल से संपर्क कर सकते हैं।",
      },
      {
        question: "यदि मैं रद्द करता हूं तो मेरे QR कोड का क्या होता है?",
        answer:
          "यदि आप अपनी सदस्यता रद्द करने का निर्णय लेते हैं, तो आपके सभी QR कोड अगले महीने समाप्त होने तक सक्रिय रहेंगे। यदि आप अपने आँकड़ों तक पहुँचना चाहते हैं या अधिक QR कोड जनरेट करना चाहते हैं, तो अपनी सदस्यता का नवीनीकरण करें।",
      },
      {
        question: "क्या मैं बाद में किसी भिन्न योजना पर स्विच कर सकता हूँ?",
        answer:
          "हाँ, आप किसी भी समय अपने खाते को अपग्रेड कर सकते हैं। फिर हम आपकी वर्तमान योजना दर के अनुसार पैकेज मूल्य को कम या बढ़ा देंगे।",
      },
      {
        question:
          "क्या आपके पास गैर-लाभकारी या शैक्षणिक संस्थानों के लिए कोई छूट है?",
        answer: "हाँ! अधिक जानकारी के लिए कृपया हमसे संपर्क करें।",
      },
    ],
  },
  {
    locale: "id",
    data: [
      {
        question: "Berapa lama masa uji coba gratisnya?",
        answer:
          "Bagi mereka yang menggunakan akun gratis, mungkin ada periode penggunaan tertentu. Saat ini, kami tidak memiliki rencana untuk membatasi masa uji coba.",
      },
      {
        question: "Bisakah saya mencoba QR Dove secara gratis?",
        answer:
          "Tentu saja! Cukup buat akun gratis Anda untuk memulai. Anda akan mendapatkan dua kode QR dinamis gratis dan mencoba restoran online kami. Jika Anda suka, Anda dapat terus menggunakan QR Dove Pro dengan memberikan detail pembayaran Anda.",
      },
      {
        question: "Opsi pembayaran apa yang Anda terima?",
        answer:
          "Kami menerima pembayaran melalui Paypal atau kartu pembayaran (Visa, Mastercard, dan lainnya) melalui Paypal. Biaya akan dalam USD, dan semua harga sudah termasuk pajak pertambahan nilai.",
      },
      {
        question: "Kapan paket saya siap digunakan?",
        answer:
          "Segera setelah Anda memilih paket Anda, masukkan detail pembayaran Anda dan lakukan pemesanan! Sangat mudah.",
      },
      {
        question: "Berapa lama jangka waktu berlangganan?",
        answer: "Bulanan atau tahunan",
      },
      {
        question: "Bagaimana cara membatalkan langganan saya?",
        answer:
          "Anda dapat berhenti berlangganan kapan saja di akun Anda. Atau dapat mengirim email menghubungi kami.",
      },
      {
        question: "Apa yang terjadi pada Kode QR saya jika saya membatalkan?",
        answer:
          "Jika Anda memutuskan untuk membatalkan langganan Anda, semua Kode QR Anda akan tetap aktif hingga kedaluwarsa bulan depan. Jika Anda ingin mengakses statistik Anda atau menghasilkan lebih banyak Kode QR, perbarui langganan Anda.",
      },
      {
        question: "Bisakah saya beralih ke paket lain nanti?",
        answer:
          "Ya, Anda dapat meningkatkan akun Anda kapan saja. Kami kemudian akan mengurangi atau menambah harga paket sesuai dengan tarif paket Anda saat ini.",
      },
      {
        question:
          "Apakah Anda memiliki diskon untuk nirlaba atau lembaga pendidikan?",
        answer: "Ya! Silakan hubungi kami untuk informasi lebih lanjut.",
      },
    ],
  },
  {
    locale: "th",
    data: [
      {
        question: "ระยะเวลาทดลองใช้ฟรีนานเท่าใด",
        answer:
          "สำหรับผู้ที่ใช้บัญชีฟรี อาจมีระยะเวลาการใช้งานที่แน่นอน ปัจจุบันเรายังไม่มีแผนที่จะจำกัดระยะเวลาทดลองใช้",
      },
      {
        question: "ฉันสามารถลองใช้ QR Dove ได้ฟรีหรือไม่",
        answer:
          "แน่นอน! เพียงสร้างบัญชีฟรีเพื่อเริ่มต้นใช้งาน คุณจะได้รับรหัส QR แบบไดนามิกฟรีสองรหัสและทดลองใช้ร้านอาหารออนไลน์ของเรา หากคุณชอบ คุณสามารถใช้ QR Dove Pro ต่อไปได้โดยแจ้งรายละเอียดการชำระเงินของคุณให้เราทราบ",
      },
      {
        question: "คุณรับตัวเลือกการชำระเงินแบบใดบ้าง",
        answer:
          "เรารับชำระเงินผ่าน Paypal หรือบัตรชำระเงิน (Visa, Mastercard และอื่นๆ) ผ่าน Paypal ค่าใช้จ่ายจะเป็นสกุล USD และราคาทั้งหมดรวมภาษีมูลค่าเพิ่มแล้ว",
      },
      {
        question: "เมื่อไหร่แผนของฉันพร้อมใช้งาน",
        answer:
          "ทันทีที่คุณเลือกแผนของคุณแล้ว ให้ป้อนรายละเอียดการชำระเงินของคุณและทำการสั่งซื้อ! มันง่ายมาก",
      },
      {
        question: "ระยะเวลาการสมัครสมาชิกนานเท่าใด",
        answer: "รายเดือนหรือรายปี",
      },
      {
        question: "ฉันจะยกเลิกการสมัครสมาชิกได้อย่างไร",
        answer:
          "คุณสามารถยกเลิกการสมัครสมาชิกได้ทุกเมื่อในบัญชีของคุณ หรือสามารถส่งอีเมลติดต่อเราได้",
      },
      {
        question: "จะเกิดอะไรขึ้นกับ QR Code ของฉันหากฉันยกเลิก",
        answer:
          "หากคุณตัดสินใจยกเลิกการสมัครสมาชิก รหัส QR ทั้งหมดของคุณจะยังคงใช้งานได้จนกว่าจะหมดอายุในเดือนหน้า หากคุณต้องการเข้าถึงสถิติของคุณหรือสร้างรหัส QR เพิ่มเติม โปรดต่ออายุการสมัครสมาชิกของคุณ",
      },
      {
        question: "ฉันสามารถเปลี่ยนไปใช้แผนอื่นได้ในภายหลังหรือไม่",
        answer:
          "ได้ คุณสามารถอัปเกรดบัญชีของคุณได้ทุกเมื่อ จากนั้นเราจะลดหรือเพิ่มราคาแพ็กเกจตามอัตราแผนปัจจุบันของคุณ",
      },
      {
        question:
          "คุณมีส่วนลดสำหรับองค์กรไม่แสวงหาผลกำไรหรือสถาบันการศึกษาหรือไม่",
        answer: "ใช่! โปรดติดต่อเราเพื่อขอข้อมูลเพิ่มเติม",
      },
    ],
  },
  {
    locale: "tr",
    data: [
      {
        question: "Ücretsiz deneme süresi ne kadar?",
        answer:
          "Ücretsiz hesap kullananlar için belirli bir kullanım süresi olabilir. Şu anda deneme süresini sınırlama gibi bir planımız yok.",
      },
      {
        question: "QR Dove'u ücretsiz deneyebilir miyim?",
        answer:
          "Elbette! Başlamak için ücretsiz hesabınızı oluşturmanız yeterli. İki adet ücretsiz dinamik QR kod alacaksınız ve çevrimiçi restoranımızı deneyebileceksiniz. Dilerseniz, ödeme bilgilerinizi sağlayarak QR Dove Pro'yu kullanmaya devam edebilirsiniz.",
      },
      {
        question: "Hangi ödeme seçeneklerini kabul ediyorsunuz?",
        answer:
          "Paypal aracılığıyla Paypal veya ödeme kartları (Visa, Mastercard ve daha fazlası) ile ödeme kabul ediyoruz. Maliyetler USD cinsinden olacak ve tüm fiyatlara katma değer vergisi dahildir.",
      },
      {
        question: "Planım ne zaman kullanıma hazır olacak?",
        answer:
          "Planınızı seçtikten hemen sonra, ödeme bilgilerinizi girin ve siparişinizi verin! Çok kolay.",
      },
      {
        question: "Abonelik şartları ne kadar süreyle geçerli?",
        answer: "Aylık veya yıllık",
      },
      {
        question: "Aboneliğimi nasıl iptal edebilirim?",
        answer:
          "Hesabınızda istediğiniz zaman aboneliğinizi iptal edebilirsiniz. Veya bizimle e-posta yoluyla iletişime geçebilirsiniz.",
      },
      {
        question: "İptal edersem QR Kodlarıma ne olur?",
        answer:
          "Aboneliğinizi iptal etmeye karar verirseniz, tüm QR Kodlarınız gelecek ay sona erene kadar aktif kalacaktır. İstatistiklerinize erişmek veya daha fazla QR Kodu oluşturmak istiyorsanız, aboneliğinizi yenileyin.",
      },
      {
        question: "Daha sonra farklı bir plana geçebilir miyim?",
        answer:
          "Evet, hesabınızı istediğiniz zaman yükseltebilirsiniz. Daha sonra paket fiyatını mevcut plan oranınıza göre düşüreceğiz veya artıracağız.",
      },
      {
        question:
          "Kâr amacı gütmeyen kuruluşlar veya eğitim kurumları için indirimleriniz var mı?",
        answer: "Evet! Daha fazla bilgi için lütfen bizimle iletişime geçin.",
      },
    ],
  },
];
const { locale, localeProperties } = useI18n();
const questions =
  ListQuestion.find((item) => item.locale == locale.value)?.data ??
  ListQuestion[0].data;

const dataLocale = new DataLocale([
  {
    locale: "en",
    title: 'Frequently Asked <span class="down-mark-line">Questions</span>',
  },
  {
    locale: "vi",
    title: "Các câu hỏi thường gặp",
  },
  {
    locale: "es",
    title: 'Preguntas <span class="down-mark-line">Frecuentes</span>',
  },
  {
    locale: "fr",
    title: 'Questions <span class="down-mark-line">Fréquentes</span>',
  },
  {
    locale: "de",
    title: 'Häufig gestellte <span class="down-mark-line">Fragen</span>',
  },
  {
    locale: "it",
    title: 'Domande <span class="down-mark-line">Frequenti</span>',
  },
  {
    locale: "nl",
    title: 'Veelgestelde <span class="down-mark-line">Vragen</span>',
  },
  {
    locale: "ja",
    title: 'よくある<span class="down-mark-line">質問</span>',
  },
  {
    locale: "zh",
    title: '常见<span class="down-mark-line">问题</span>',
  },
]);
export declare type questionsSchema = Array<any>;
var questionsSchema: questionsSchema = [];

questions.forEach((item) => {
  questionsSchema.push(
    defineQuestion({
      question: item.question,
      answer: item.answer,
      inLanguage: localeProperties.value.iso,
    })
  );
});
useSchemaOrg(questionsSchema);
</script>
